import React, { useState, useEffect, useMemo, useRef } from 'react';
import { spanish } from '../../api';
import { playSpanish, stopSpanishAudio } from '../../utils/spanishAudio';
import './Spanish.css';

const shuffle = (arr) => {
  const a = [...arr];
  for (let i = a.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [a[i], a[j]] = [a[j], a[i]];
  }
  return a;
};

/**
 * One practice session. For each word:
 *   new word      -> a "learn" card (hear it, see it in a sentence), then the quiz
 *   known word    -> straight to the quiz
 * Quiz alternates: hear/see Spanish -> pick the English, and English -> pick the Spanish.
 * Every answer is saved, which drives the spaced repetition on the server.
 *
 * props: unit (optional), itemIds (optional - only these words), onClose(result)
 */
export default function SpanishPractice({ unit, itemIds, onClose }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [session, setSession] = useState([]);
  const [pool, setPool] = useState([]);
  const [index, setIndex] = useState(0);
  const [stage, setStage] = useState('learn'); // learn | quiz | result
  const [picked, setPicked] = useState(null);
  const [score, setScore] = useState({ right: 0, xp: 0, learned: 0 });
  const busy = useRef(false);

  useEffect(() => {
    let cancelled = false;
    spanish.practice(unit, itemIds)
      .then(res => {
        if (cancelled) return;
        const s = res.data.session || [];
        setSession(s);
        setPool(res.data.pool || []);
        setStage(s.length && s[0].status === 'new' ? 'learn' : 'quiz');
      })
      .catch(() => !cancelled && setError('Could not load practice. Please try again.'))
      .finally(() => !cancelled && setLoading(false));
    return () => { cancelled = true; stopSpanishAudio(); };
  }, [unit, itemIds]);

  const item = session[index];
  const direction = index % 2 === 0 ? 'es-en' : 'en-es';

  // 3 wrong options + the right one
  const options = useMemo(() => {
    if (!item) return [];
    const wrong = shuffle(pool.filter(p => p._id !== item._id)).slice(0, 3);
    return shuffle([item, ...wrong]).map(o => ({
      _id: o._id,
      label: direction === 'es-en' ? o.en : o.es
    }));
  }, [item, pool, direction]);

  const next = () => {
    setPicked(null);
    stopSpanishAudio();
    if (index + 1 >= session.length) { setStage('result'); return; }
    const nextItem = session[index + 1];
    setIndex(index + 1);
    setStage(nextItem.status === 'new' ? 'learn' : 'quiz');
  };

  const answer = async (opt) => {
    if (picked || busy.current) return;
    busy.current = true;
    const correct = opt._id === item._id;
    setPicked({ id: opt._id, correct });
    if (direction === 'en-es') playSpanish(item);
    try {
      const res = await spanish.answer(item._id, correct);
      setScore(s => ({
        right: s.right + (correct ? 1 : 0),
        xp: s.xp + (res.data.xp || 0),
        learned: s.learned + (res.data.leveledUp ? 1 : 0)
      }));
    } catch (e) {
      setError('Could not save that answer.');
    } finally {
      busy.current = false;
    }
  };

  const close = () => { stopSpanishAudio(); onClose && onClose(score); };

  return (
    <div className="sp-overlay" onClick={close}>
      <div className="sp-sheet" onClick={(e) => e.stopPropagation()}>
        <div className="sp-sheet-top">
          <div className="sp-progress"><i style={{ width: `${session.length ? (stage === 'result' ? 100 : (index / session.length) * 100) : 0}%` }} /></div>
          <button className="sp-x" onClick={close} aria-label="Close">✕</button>
        </div>

        {loading && <div className="sp-empty">Loading…</div>}
        {error && <div className="sp-error">{error}</div>}

        {!loading && session.length === 0 && !error && (
          <div className="sp-done">
            <div className="sp-trophy">🎉</div>
            <h3>All caught up!</h3>
            <p className="sp-sub">Nothing is due right now. Come back later and your words will be waiting.</p>
            <button className="sp-btn" onClick={close}>Close</button>
          </div>
        )}

        {!loading && item && stage === 'learn' && (
          <div>
            <div className="sp-prompt">New word</div>
            <div className="sp-big">
              <div className="sp-es">{item.es}</div>
              <div className="sp-en">{item.en}</div>
              {item.example && <div className="sp-ex">“{item.example}”<br />{item.exampleEn}</div>}
              {item.tip && <div className="sp-tip">💡 {item.tip}</div>}
            </div>
            <div style={{ display: 'flex', gap: 10, justifyContent: 'center', marginTop: 16 }}>
              <button className="sp-btn sp-btn--ghost" onClick={() => playSpanish(item)}>🔊 Listen</button>
              <button className="sp-btn" onClick={() => { setStage('quiz'); }}>Got it · Quiz me</button>
            </div>
          </div>
        )}

        {!loading && item && stage === 'quiz' && (
          <div>
            <div className="sp-prompt">{direction === 'es-en' ? 'What does this mean?' : 'How do you say…'}</div>
            <div className="sp-big">
              {direction === 'es-en' ? (
                <>
                  <div className="sp-es">{item.es}</div>
                  <button className="sp-btn sp-btn--ghost sp-btn--sm" style={{ marginTop: 8 }} onClick={() => playSpanish(item)}>🔊 Listen</button>
                </>
              ) : (
                <div className="sp-en" style={{ fontSize: 22, fontWeight: 800 }}>{item.en}</div>
              )}
            </div>
            <div className="sp-options">
              {options.map(o => {
                const isPicked = picked && picked.id === o._id;
                const isRight = picked && o._id === item._id;
                return (
                  <button key={o._id} className={`sp-option ${isRight ? 'sp-option--right' : ''} ${isPicked && !picked.correct ? 'sp-option--wrong' : ''}`}
                    onClick={() => answer(o)} disabled={!!picked}>
                    {o.label}
                  </button>
                );
              })}
            </div>
            {picked && (
              <>
                <div className={`sp-feedback ${picked.correct ? 'sp-feedback--ok' : 'sp-feedback--no'}`}>
                  {picked.correct ? '¡Muy bien! ✓' : <>Not quite - it's <b>{direction === 'es-en' ? item.en : item.es}</b></>}
                </div>
                <div style={{ textAlign: 'center', marginTop: 12 }}>
                  <button className="sp-btn" onClick={next}>{index + 1 >= session.length ? 'Finish' : 'Next'}</button>
                </div>
              </>
            )}
          </div>
        )}

        {!loading && stage === 'result' && (
          <div className="sp-done">
            <div className="sp-trophy">🏆</div>
            <h3>¡Buen trabajo!</h3>
            <p className="sp-sub">
              {score.right} of {session.length} right · +{score.xp} XP
              {score.learned > 0 && <><br />{score.learned} word{score.learned !== 1 ? 's' : ''} now learned</>}
            </p>
            <button className="sp-btn" onClick={close}>Done</button>
          </div>
        )}
      </div>
    </div>
  );
}
